// "Search is open" — the ONE notion of search focus the shell reads.
//
// Search is a focused mode: while it's open the surrounding chrome gets out of the way (the
// Follow-ups strip under the SmartBox and the Global timeline side panel) so only the search is
// on screen. Kept here as a plain function so the rule lives in one place and is testable
// without a store.
//
// Today only the globe web-search (research.searchFocus) opens it. The keyword search
// (timelineSearch.keywordActive) is DELIBERATELY excluded and must stay that way until its
// results have somewhere to land: `SidePanel/SearchPanel.vue` is currently commented out of
// `SidePanel/index.vue`, so letting a bare keyword clear the chrome would leave a blank screen —
// worse than the stray "all caught up" strip this rule exists to remove. The flag is still passed
// in (and still maintained by the timelineSearch store) so wiring it up is a one-word change the
// day the results panel comes back.

export const isSearchOpen = ({ searchFocus, keywordActive } = {}) => {
  void keywordActive // see above — kept in the shape, not in the rule
  return !!searchFocus
}

// --- Who owns the middle column while a search is armed / running ---
//
// The middle is either the research workspace or the page's own content — never neither (that gap
// was the blank-middle bug). The rule depends on WHERE we are:
//
//  • home            — armed shows the workspace + its empty state; a submitted search keeps it.
//  • listing module  — inbox / notes / tasks / events / contacts / files: armed shows the workspace
//                      + empty state, exactly like home. Turning the globe off gives the listing
//                      straight back (results stay in the store, so re-arming brings them back);
//                      a stale search from a previous page must NOT hijack a listing you navigated to.
//  • an ongoing      — the story is what the user is reading, so arming the globe must NOT blank it.
//                      The workspace only takes over once a search has actually RUN for THAT ongoing
//                      (research.active + targetOngoingId). While merely armed, StoryView keeps the
//                      body and only its listing header switches to globe + "Web search".
//  • single email / settings / anything else — never; those are detail or full-height pages.
export const HOME_PATHS = ['/', '/home']
export const MODULE_PREFIXES = ['/emails', '/tasks', '/events', '/notes', '/contacts', '/files']
// Detail views under a module prefix: an open email is read like a story, so it is not blanked.
// …nor is an open task, note or event (the entity readers, /<module>/item/:id — 2026-10-02).
const DETAIL_PATTERNS = [/^\/emails\/timeline\//, /^\/emails\/inbox\/[^/]+/, /^\/emails\/[^/]+\/reply/, /^\/(tasks|notes|events)\/item\//]

export const isHomePath = (path) => HOME_PATHS.includes(String(path || ''))

// The ongoing id in `/ongoing/:id`, or null anywhere else.
export const ongoingIdFromPath = (path) => (String(path || '').match(/^\/ongoing\/([^/]+)/) || [])[1] || null

/**
 * Do two references name the SAME ongoing? (busy-my-web#395)
 *
 * Ongoing ids are Guids, and a Guid is not a case-sensitive string: the one in the URL is whatever
 * was typed or linked, the one in the store is whatever the server serialized, and the two are
 * routinely written in different cases. Every `===` between them is therefore a coin flip, and the
 * one that matters is the "this story gained something" signal (`ongoing-advanced`): miss it and
 * the page never re-reads the story, so the thing the user just created is not in the Briefing
 * until they leave and come back — which is exactly what was reported.
 *
 * Blank on either side is not a match: "no ongoing" is not the same ongoing as any ongoing.
 */
export const sameOngoing = (a, b) => {
  const x = String(a ?? '').trim().toLowerCase()
  const y = String(b ?? '').trim().toLowerCase()
  return !!x && x === y
}

// A listing module page — where the empty state is allowed to replace the listing.
export const isModulePath = (path) => {
  const p = String(path || '')
  if (DETAIL_PATTERNS.some(re => re.test(p))) return false
  return MODULE_PREFIXES.some(prefix => p === prefix || p.startsWith(`${prefix}/`))
}

export const researchOwnsMiddle = ({ path, searchArmed, researchActive, targetOngoingId } = {}) => {
  const ongoingId = ongoingIdFromPath(path)
  if (ongoingId) return !!researchActive && targetOngoingId === ongoingId
  if (isHomePath(path)) return !!searchArmed || !!researchActive
  if (isModulePath(path)) return !!searchArmed
  return false
}

export default isSearchOpen
